import React, { useState } from "react";
import { Calendar, ChevronRight, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { toast } from "sonner";

const RECOVERY_PROGRAMS = [
  { id: "AA", label: "Alcoholics Anonymous (AA)" },
  { id: "NA", label: "Narcotics Anonymous (NA)" },
  { id: "CA", label: "Cocaine Anonymous (CA)" },
  { id: "SMART", label: "SMART Recovery" },
  { id: "Refuge Recovery", label: "Refuge Recovery" },
  { id: "Celebrate Recovery", label: "Celebrate Recovery" },
  { id: "LifeRing", label: "LifeRing" },
  { id: "Therapy Only", label: "Therapy / Counseling" },
  { id: "Other", label: "Other Program" },
];

const TERMINOLOGY_OPTIONS = [
  { id: "clean", label: '"Clean" (commonly used in NA)' },
  { id: "sober", label: '"Sober" (commonly used in AA)' },
  { id: "in_recovery", label: '"In Recovery" (general term)' },
];

const MEETING_FREQUENCY_OPTIONS = [
  { id: "daily", label: "Daily", icon: "📅" },
  { id: "multiple_weekly", label: "Multiple times a week", icon: "📆" },
  { id: "weekly", label: "Weekly", icon: "🗓️" },
  { id: "monthly", label: "Monthly", icon: "📋" },
  { id: "occasionally", label: "Occasionally", icon: "🌙" },
  { id: "rarely", label: "Rarely/Never", icon: "❌" },
];

const RecoveryBasicsStep = ({ onNext }) => {
  const [birthday, setBirthday] = useState("");
  const [cleanDate, setCleanDate] = useState("");
  const [programs, setPrograms] = useState([]);
  const [terminology, setTerminology] = useState("");
  const [meetingFrequency, setMeetingFrequency] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blocked, setBlocked] = useState(null);

  // Calculate age from birthday
  const calculateAge = (birthDate) => {
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      age--;
    }
    return age;
  };

  const toggleProgram = (programId) => {
    setPrograms(prev => 
      prev.includes(programId)
        ? prev.filter(p => p !== programId)
        : [...prev, programId]
    );
  };

  const handleSubmit = async () => {
    if (!birthday) {
      toast.error("Please enter your birthday");
      return;
    }
    const age = calculateAge(birthday);
    if (age < 18) {
      toast.error("You must be 18 or older to use RecoveryMatch");
      return;
    }
    if (!cleanDate) {
      toast.error("Please select your recovery start date");
      return;
    }
    if (programs.length === 0) {
      toast.error("Please select at least one recovery program");
      return;
    }
    if (!terminology) {
      toast.error("Please select your preferred terminology");
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.submitRecoveryBasics({
        birthday: new Date(birthday).toISOString(),
        clean_date: new Date(cleanDate).toISOString(),
        recovery_programs: programs,
        recovery_terminology: terminology,
        meeting_frequency: meetingFrequency || "occasionally"
      });

      if (response.data.blocked) {
        setBlocked(response.data);
        return;
      }

      onNext(response.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  // Show blocked message if user has less than 90 days
  if (blocked) {
    return (
      <div className="bg-white rounded-2xl p-6 text-center">
        <div className="w-16 h-16 bg-[#FAB710]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-[#FAB710]" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-3">Keep Going!</h2>
        <p className="text-gray-600 mb-4">{blocked.message}</p>
        {blocked.days_until_eligible && (
          <div className="bg-[#1C3A7A]/10 rounded-xl p-4 mb-4">
            <p className="text-[#1C3A7A] font-semibold">
              {blocked.days_until_eligible} days until you can join
            </p>
          </div>
        )}
        <p className="text-gray-500 text-sm">
          We&apos;ll be here when you&apos;re ready. Your journey matters.
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-2">Your Recovery Journey</h2>
      <p className="text-gray-600 mb-6">This helps us match you with people at similar stages</p>

      {/* Birthday */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          When is your birthday?
        </label>
        <input
          type="date"
          value={birthday}
          onChange={(e) => setBirthday(e.target.value)}
          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
        />
        <p className="text-gray-500 text-sm mt-1">
          You must be 18 or older to join
        </p>
      </div>

      {/* Clean Date */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          When did your recovery journey begin?
        </label>
        <input
          type="date"
          value={cleanDate}
          onChange={(e) => setCleanDate(e.target.value)}
          max={new Date().toISOString().split('T')[0]}
          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#1C3A7A] focus:border-transparent"
        />
        <p className="text-gray-500 text-sm mt-1">
          We require at least 90 days of recovery to join
        </p>
      </div>

      {/* Recovery Programs */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          What recovery programs are you part of? (Select all that apply)
        </label>
        <div className="space-y-2">
          {RECOVERY_PROGRAMS.map(program => (
            <button
              key={program.id}
              onClick={() => toggleProgram(program.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                programs.includes(program.id)
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded border mr-3 ${
                programs.includes(program.id)
                  ? 'bg-[#1C3A7A] border-[#1C3A7A]'
                  : 'border-gray-300'
              }`}>
                {programs.includes(program.id) && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </span>
              {program.label}
            </button>
          ))}
        </div>
      </div>

      {/* Terminology Preference */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          How do you describe your recovery time?
        </label>
        <p className="text-gray-500 text-sm mb-3">
          Different recovery communities use different terms. Choose the one that feels right for you.
        </p>
        <div className="space-y-2">
          {TERMINOLOGY_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setTerminology(option.id)}
              className={`w-full px-4 py-3 rounded-xl border-2 text-left transition-all ${
                terminology === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <span className={`inline-flex items-center justify-center w-5 h-5 rounded-full border mr-3 ${
                terminology === option.id
                  ? 'bg-[#1C3A7A] border-[#1C3A7A]'
                  : 'border-gray-300'
              }`}>
                {terminology === option.id && (
                  <span className="w-2 h-2 bg-white rounded-full" />
                )}
              </span>
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Meeting Frequency */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
          <Clock className="w-4 h-4" />
          How often do you attend meetings?
        </label>
        <p className="text-gray-500 text-sm mb-3">
          This is optional but helps with compatibility matching.
        </p>
        <div className="grid grid-cols-2 gap-2">
          {MEETING_FREQUENCY_OPTIONS.map(option => (
            <button
              key={option.id}
              onClick={() => setMeetingFrequency(option.id)}
              className={`px-3 py-3 rounded-xl border-2 text-left transition-all flex items-center gap-2 ${
                meetingFrequency === option.id
                  ? 'border-[#1C3A7A] bg-[#1C3A7A]/5 text-[#1C3A7A]'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              <span>{option.icon}</span>
              <span className="text-sm font-medium">{option.label}</span>
            </button>
          ))}
        </div>
      </div>

      <Button
        onClick={handleSubmit}
        disabled={submitting || !birthday || !cleanDate || programs.length === 0 || !terminology}
        className="w-full h-12 bg-[#1C3A7A] hover:bg-[#15305f] text-white font-semibold rounded-xl"
      >
        {submitting ? (
          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
        ) : (
          <>
            Continue
            <ChevronRight className="w-5 h-5 ml-2" />
          </>
        )}
      </Button>
    </div>
  );
};

export default RecoveryBasicsStep;
